import { useState, useContext } from 'react';
import { WebRTCContext } from './contexts/WebRTCContext';
import { useMIDI } from './contexts/MIDIContext';

const midiCCMappings = {
  Filter: [
    { label: "Filter Cutoff", midiCC: 19 },
    { label: "Filter Resonance", midiCC: 21 },
    { label: "Filter Envelope Amount", midiCC: 22 },
    { label: "Filter Keyboard Tracking", midiCC: 20 },
    { label: "Filter Velocity Sensitivity", midiCC: 89 },
  ],
  VCO: [
    { label: "VCO 1 Waveform", midiCC: 70 },
    { label: "VCO 2 Waveform", midiCC: 71 },
    { label: "VCO 2 Frequency Offset", midiCC: 17 },
    { label: "VCO 2 Beat Frequency", midiCC: 18 },
    { label: "VCO 1 Level", midiCC: 15 },
    { label: "VCO 2 Level", midiCC: 16 },
  ],
  Glide: [
    { label: "Glide On/Off", midiCC: 65, type: "switch" },
    { label: "Glide Rate", midiCC: 5 },
    { label: "Glide Type", midiCC: 92, type: "segmented" },
    { label: "Legato Glide", midiCC: 83, type: "switch" },
  ],
  Amp: [
    { label: "Amp Envelope Attack", midiCC: 28 },
    { label: "Amp Envelope Decay/Release", midiCC: 29 },
    { label: "Amp Envelope Sustain", midiCC: 30 },
    { label: "Output Volume", midiCC: 7 },
    { label: "Volume Velocity Sensitivity", midiCC: 90 },
  ],
  LFO: [
    { label: "LFO Rate", midiCC: 3 },
    { label: "LFO Amount to VCO", midiCC: 13 },
    { label: "LFO Amount to VCF", midiCC: 12 },
    { label: "LFO MIDI Sync", midiCC: 87, type: "switch" },
    { label: "LFO Sync Clock Division", midiCC: 86 },
    { label: "LFO Key Trigger", midiCC: 82, type: "switch" },
  ],
  Misc: [
    { label: "External Audio Input Level", midiCC: 27 },
    { label: "Note Sync", midiCC: 81, type: "switch" },
    { label: "Modulation Wheel", midiCC: 1 },
  ],
};

export default function MoogSirinUI() {
  const [midiDevices, setMidiDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Get MIDI state and functions from the MIDIContext
  const {
    midiAccess,
    midiOutputs,
    selectedOutput,
    setSelectedOutput,
    requestMIDIAccess,
    sendMIDI
  } = useMIDI();

  const { sendData } = useContext(WebRTCContext);

  const handleDeviceChange = (e) => {
    const device = midiDevices.find((d) => d.id === e.target.value);
    setSelectedDevice(device);
  };

  return (
    <div style={{ padding: '1rem', border: '1px solid #ccc', margin: '1rem', backgroundColor: '#FBE64D' }}>
      <h2>Moog Sirin Parameter Controls</h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '10px',
        width: '100%'
      }}>
        {Object.entries(midiCCMappings).map(([section, controls]) => (
          <div key={section} style={{
            padding: '20px',
            backgroundColor: '#c3d9e4',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              {section}
            </h2>
            {controls.map(({ label, midiCC, type }) =>
              type === "switch" ? (
                <ToggleSwitch key={midiCC} label={label} midiCC={midiCC} device={selectedDevice} />
              ) : type === "segmented" ? (
                <SegmentedControl key={midiCC} label={label} midiCC={midiCC} device={selectedDevice} />
              ) : (
                <Knob key={midiCC} label={label} midiCC={midiCC} device={selectedDevice} />
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );


  function Knob({ label, midiCC, device }) {
    const [value, setValue] = useState(64);

    const handleChange = (e) => {
      setValue(e.target.value);
      sendMIDIMessage(midiCC, e.target.value, device);
    };

    return (
      <div className="flex flex-col items-start w-48">
        <label className="text-lg mb-2 text-left w-full">{label}</label>
        <input type="range" min="0" max="127" value={value} onChange={handleChange} className="w-48" />
      </div>
    );
  }

  function ToggleSwitch({ label, midiCC, device }) {
    const [isOn, setIsOn] = useState(false);

    const handleToggle = () => {
      const newValue = isOn ? 0 : 127;
      setIsOn(!isOn);
      sendMIDIMessage(midiCC, newValue, device);
    };

    return (
      <div className="flex flex-col items-start w-48">
        <label className="text-lg mb-2 text-left w-full">{label}</label>
        <button
          onClick={handleToggle}
          className={`w-48 h-8 text-sm p-1 rounded-full ${isOn ? "bg-green-400 text-black" : "bg-gray-300 text-black"}`}
        >
          {isOn ? "On" : "Off"}
        </button>
      </div>
    );
  }

  function SegmentedControl({ label, midiCC, device }) {
    const [selectedValue, setSelectedValue] = useState(0);

    const handleSelect = (value) => {
      setSelectedValue(value);
      sendMIDIMessage(midiCC, value, device);
    };

    return (
      <div className="flex flex-col items-start w-48">
        <label className="text-lg mb-2 text-left w-full">{label}</label>
        <div className="flex w-48">
          {["LCR", "LCT", "EXP"].map((label, index) => (
            <button
              key={index}
              onClick={() => handleSelect(index)}
              className={`flex-1 p-2 text-sm rounded ${selectedValue === index ? "bg-green-400 text-black" : "bg-gray-300 text-black"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  function sendMIDIMessage(cc, value, device) {
    const midiMessage = [0xB0, cc, value];
    sendData(midiMessage);
  }
}